import type {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import type {PricedItem, PricingNodegraph} from "#pricing/types/pricing-types"
import type {PriceList} from "#pricing/nodes/price-list"
import {VariantConditionNode} from "#pricing/nodes/variant-condition-node"
import {PricingNode, registerPricingGraphNode} from "#pricing/pricing-node"
import type {PricingContext} from "#pricing/pricing-context"
import type {Evaluator, GraphNodeDefinition, ParameterValue} from "@cm/graph-v3"
import {graphNode, parameterValue} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export type PriceContainerParameters = {
    description: string //human-readable name, usually derived from the customer's catalog
    uniqueId: string //unique id, which is usually derived from the customer's catalog
    subPrices: PriceList //a list of related prices that depend on the current node
    condition?: ParameterValue<boolean, PricingContext>
}

const priceContainerDefinition = {
    nodeClass: "PriceContainer",
    validation: {
        parameters: z.object({
            description: z.string(),
            uniqueId: z.string(),
            subPrices: graphNode<PriceList>("PriceList"),
            condition: z.optional(parameterValue(z.boolean())),
        }),
        returns: z.array(z.custom<PricedItem>(() => true)),
    },
} satisfies GraphNodeDefinition<PricedItem[], PriceContainerParameters>

/**This is a named container for prices. It can be used as structuring element and can be used to enforce e.g. mutually exclusive conditions during verification. Can also be displayed in the ui. */
@registerPricingGraphNode
export class PriceContainer extends PricingNode<PricedItem[], PriceContainerParameters> {
    public override get definition() {
        return priceContainerDefinition
    }

    override run = async (evaluator: Evaluator<PricingContext>) => {
        if (this.parameters.condition) {
            const condition = await evaluator.get(this.parameters.condition)
            if (!condition) return []
        }

        const subPrices = await evaluator.get(this.parameters.subPrices)
        return subPrices.flat()
    }

    getUniqueId(): string {
        return this.parameters.uniqueId
    }

    getDescription(): string | undefined {
        return this.parameters.description
    }

    getSubprices(): PricingNodegraph[] {
        if (!this.parameters.subPrices) return []
        return this.parameters.subPrices.parameters.list
    }

    addDependency(configGroupNode: ConfigurationGroupNode, variantId: string, variantName: string) {
        if (!(this.parameters.condition instanceof VariantConditionNode)) throw new Error("Node does not accept conditions")
        this.parameters.condition.addDependency(configGroupNode, variantId, variantName)
    }

    removeDependency(variantId: string) {
        if (!(this.parameters.condition instanceof VariantConditionNode)) throw new Error("Node does not accept conditions")
        this.parameters.condition.removeDependency(variantId)
    }

    canAddDependency(groupId: string, variantId: string) {
        if (!(this.parameters.condition instanceof VariantConditionNode)) return false
        return this.parameters.condition.canAddDependency(groupId, variantId)
    }

    getVariantIds(): string[] {
        if (!(this.parameters.condition instanceof VariantConditionNode)) return []
        return this.parameters.condition.getVariantIds()
    }
}
